import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import {
  SaveActivityDto,
  SaveCategoryDto,
  ScheduleActivityDto,
  ScheduleCategoryDto,
  WeeklyScheduleDto,
  WeeklyScheduleSummaryDto,
} from './weekly-schedule.dto';

/**
 * The Weekly Schedule API — `docs/backend-spec-weekly-schedule.md` §4.
 *
 * ONE ACTIVITY PER CALL, deliberately. There is no whole-week `PUT` and none
 * should be added: a whole-week write would re-send and re-authorise the entire
 * week for every small edit, a failure anywhere would rewrite everything, and two
 * devices touching one week would clobber each other wholesale instead of at the
 * single activity that actually collided.
 *
 * The server owns the guid. `POST` returns the created record and the client
 * holds the id it is given rather than inventing one.
 */
@Injectable({ providedIn: 'root' })
export class WeeklyScheduleApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  /** Whether a backend is configured. Empty `apiUrl` = no API yet. */
  available(): boolean {
    return !!this.config.apiUrl;
  }

  /**
   * `apiUrl` ALREADY carries the version prefix. Appending another produced
   * /api/v1/api/v1/… and a 404 against every endpoint on the Company service.
   */
  private url(path: string): string {
    return `${this.config.apiUrl.replace(/\/$/, '')}/weekly-schedules${path}`;
  }

  /**
   * One person's week.
   *
   * Never 404s for somebody who has no week — the server returns a skeleton with
   * `authored: false`, because having no week is the normal case and putting the
   * most common state behind the error path would be wrong.
   */
  get(personaCode: string): Observable<WeeklyScheduleDto> {
    return this.http.get<WeeklyScheduleDto>(this.url(`/${personaCode}`));
  }

  /** The portfolio: one row per persona, no activities and no categories. */
  list(): Observable<WeeklyScheduleSummaryDto[]> {
    return this.http.get<WeeklyScheduleSummaryDto[]>(this.url(''));
  }

  createActivity(personaCode: string, body: SaveActivityDto): Observable<ScheduleActivityDto> {
    return this.http.post<ScheduleActivityDto>(this.url(`/${personaCode}/activities`), body);
  }

  updateActivity(personaCode: string, activityGuid: string, body: SaveActivityDto): Observable<ScheduleActivityDto> {
    return this.http.patch<ScheduleActivityDto>(this.url(`/${personaCode}/activities/${activityGuid}`), body);
  }

  deleteActivity(personaCode: string, activityGuid: string): Observable<void> {
    return this.http.delete<void>(this.url(`/${personaCode}/activities/${activityGuid}`));
  }

  createCategory(personaCode: string, body: SaveCategoryDto): Observable<ScheduleCategoryDto> {
    return this.http.post<ScheduleCategoryDto>(this.url(`/${personaCode}/categories`), body);
  }

  updateCategory(personaCode: string, categoryGuid: string, body: SaveCategoryDto): Observable<ScheduleCategoryDto> {
    return this.http.patch<ScheduleCategoryDto>(this.url(`/${personaCode}/categories/${categoryGuid}`), body);
  }

  deleteCategory(personaCode: string, categoryGuid: string): Observable<void> {
    return this.http.delete<void>(this.url(`/${personaCode}/categories/${categoryGuid}`));
  }
}
